/**
 * useFeatureFlags — global kill-switch map. Distinct from
 * ``useEntitlements`` (which reflects what THIS user has via their
 * plan + grants).
 *
 * Use case: the operator can flip a capability OFF for everyone via
 * Admin → Funcionalidades. When that happens the UI must HIDE the
 * affected section entirely (no upsell, no teaser). On the other
 * hand, if the capability is globally enabled but the user's plan
 * doesn't include it, the UI shows the usual lock/teaser. The two
 * states feel similar but the upsell path is only valid in the
 * second one.
 *
 * Returns:
 *   {
 *     flags: Record<string, boolean>,
 *     isEnabled(key): boolean,  // defaults true when key missing
 *     isLoading: boolean,
 *   }
 *
 * Source: ``GET /api/public/feature-flags`` (public, no auth).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

export function useFeatureFlags() {
  const { data, isLoading } = useQuery({
    queryKey: ["public-feature-flags"],
    queryFn: async () => (await api.get("/public/feature-flags")).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const flags = data?.flags || {};
  return {
    flags,
    // Default-on: missing key ⇒ enabled. Same rule the backend uses.
    isEnabled: (key) => flags[key] !== false,
    isLoading,
  };
}
